import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import { useChat } from '../../../hooks/useChat';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const FALLBACK_ASSESSMENT = {
  overallHealth: 'Moderate',
  healthScore: 65,
  summary: 'Your goals are progressing but some need attention. Focus on consistent monthly contributions to stay on track.',
  atRiskGoals: [],
  onTrackGoals: [],
  actionPlans: [],
};

export default function GoalCoachPanel({ goals }) {
  const { user } = useAuth();
  const [assessment, setAssessment] = useState(null);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [spendingContext, setSpendingContext] = useState('');
  const [userProfile, setUserProfile] = useState(null);
  const { response, isLoading, error, sendMessage } = useChat('OPEN_AI', 'gpt-5', false);

  useEffect(() => {
    if (!user?.id) return;
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1)?.toISOString();
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59)?.toISOString();

    Promise.all([
      supabase?.from('transactions')?.select('category, amount')?.eq('user_id', user?.id)?.gte('date', start)?.lte('date', end),
      supabase?.from('user_profiles')?.select('income_range, risk_tolerance_score, full_name')?.eq('id', user?.id)?.single(),
    ])?.then(([txRes, profileRes]) => {
      const txs = txRes?.data || [];
      const byCategory = {};
      txs?.forEach(t => { byCategory[t.category || 'Other'] = (byCategory?.[t?.category || 'Other'] || 0) + Math.abs(t?.amount || 0); });
      const summary = Object.entries(byCategory)?.map(([cat, amt]) => `${cat}: $${Math.round(amt)}`)?.join(', ');
      setSpendingContext(summary);
      if (profileRes?.data) setUserProfile(profileRes?.data);
    });
  }, [user?.id]);

  const generateAssessment = useCallback(() => {
    if (!goals?.length || isLoading) return;
    setHasGenerated(true);

    const goalsData = goals?.filter(g => g?.goal_status === 'active')?.map(g => {
      const target = parseFloat(g?.target_amount) || 1;
      const current = parseFloat(g?.current_amount) || 0;
      const monthly = parseFloat(g?.monthly_contribution) || 0;
      const pct = ((current / target) * 100)?.toFixed(1);
      const remaining = target - current;
      const monthsNeeded = monthly > 0 ? Math.ceil(remaining / monthly) : null;
      const deadline = g?.deadline ? new Date(g.deadline)?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'No deadline';
      return `${g?.title}: ${pct}% complete ($${current?.toFixed(0)}/$${target?.toFixed(0)}), monthly contribution: $${monthly}, deadline: ${deadline}${monthsNeeded ? `, needs ${monthsNeeded} more months at current rate` : ''}`;
    })?.join('\n');

    const incomeRange = userProfile?.income_range || 'not specified';

    sendMessage([
      {
        role: 'system',
        content: `You are FinVisAR Goal Coach, an expert financial planning AI. Analyze financial goals and return ONLY a valid JSON object with these exact fields: "overallHealth" ("Excellent"|"Good"|"Moderate"|"At Risk"), "healthScore" (0-100 number), "summary" (1-2 sentence overall assessment), "atRiskGoals" (array of goal title strings that are behind schedule), "onTrackGoals" (array of goal title strings on track), "actionPlans" (array of objects with "goalTitle" string and "steps" array of 2-3 specific action strings). Return ONLY the JSON object.`,
      },
      {
        role: 'user',
        content: `Assess my financial goals health:\n\nActive Goals:\n${goalsData || 'No active goals'}\n\nThis month's spending: ${spendingContext || 'No spending data'}\nIncome range: ${incomeRange}\n\nIdentify which goals are at risk and provide specific action plans for each at-risk goal.`,
      },
    ], { max_completion_tokens: 1000 });
  }, [goals, isLoading, spendingContext, userProfile, sendMessage]);

  useEffect(() => {
    if (response && hasGenerated && !isLoading) {
      try {
        const match = response?.match(/\{.*\}/s);
        if (match) {
          const parsed = JSON.parse(match?.[0]);
          setAssessment(parsed);
          return;
        }
        setAssessment(FALLBACK_ASSESSMENT);
      } catch {
        setAssessment(FALLBACK_ASSESSMENT);
      }
    }
  }, [response, hasGenerated, isLoading]);

  useEffect(() => {
    if (error && hasGenerated) setAssessment(FALLBACK_ASSESSMENT);
  }, [error, hasGenerated]);

  // Auto-generate when goals load
  useEffect(() => {
    if (goals?.length > 0 && !hasGenerated && !isLoading) {
      generateAssessment();
    }
  }, [goals]);

  const healthColors = { Excellent: '#10B981', Good: '#00E5FF', Moderate: '#F59E0B', 'At Risk': '#EF4444' };
  const healthColor = healthColors?.[assessment?.overallHealth] || '#F59E0B';

  return (
    <div
      className="rounded-xl overflow-hidden mb-6"
      style={{
        background: 'linear-gradient(135deg, rgba(123,47,190,0.08) 0%, rgba(0,229,255,0.05) 100%)',
        border: '1px solid rgba(123,47,190,0.25)',
        backdropFilter: 'blur(12px)',
      }}
    >
      {/* Coach Header */}
      <div
        className="px-5 py-4 flex items-center justify-between"
        style={{ background: 'linear-gradient(135deg, rgba(123,47,190,0.15) 0%, rgba(0,229,255,0.08) 100%)', borderBottom: '1px solid rgba(123,47,190,0.2)' }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #7B2FBE, #00E5FF)', boxShadow: '0 0 16px rgba(123,47,190,0.4)' }}
          >
            <Icon name="Bot" size={18} color="#fff" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-[#F8FAFC] font-bold text-sm">FinVisAR Goal Coach</h3>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: 'rgba(0,229,255,0.15)', color: '#00E5FF', border: '1px solid rgba(0,229,255,0.25)' }}>AI</span>
            </div>
            <p className="text-[#64748B] text-xs">Personalized goal health assessment</p>
          </div>
        </div>
        <button
          onClick={generateAssessment}
          disabled={isLoading || !goals?.length}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 disabled:opacity-50"
          style={{ background: 'rgba(123,47,190,0.2)', border: '1px solid rgba(123,47,190,0.35)', color: '#A78BFA' }}
        >
          {isLoading ? (
            <><div className="w-3 h-3 border border-[#A78BFA] border-t-transparent rounded-full animate-spin" />Analyzing...</>
          ) : (
            <><Icon name="RefreshCw" size={12} />Refresh</>
          )}
        </button>
      </div>
      <div className="p-5">
        {/* Loading skeleton */}
        {isLoading && (
          <div className="space-y-4 animate-pulse">
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-full bg-[rgba(255,255,255,0.06)]" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-[rgba(255,255,255,0.06)] rounded w-1/3" />
                <div className="h-3 bg-[rgba(255,255,255,0.04)] rounded w-full" />
                <div className="h-3 bg-[rgba(255,255,255,0.04)] rounded w-4/5" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="h-16 bg-[rgba(255,255,255,0.04)] rounded-xl" />
              <div className="h-16 bg-[rgba(255,255,255,0.04)] rounded-xl" />
            </div>
          </div>
        )}

        {/* No goals state */}
        {!isLoading && !goals?.filter(g => g?.goal_status === 'active')?.length && (
          <div className="text-center py-6">
            <Icon name="Target" size={28} color="#475569" />
            <p className="text-[#64748B] text-sm mt-2">Add active goals to get AI coaching and health assessment.</p>
          </div>
        )}

        {/* Assessment */}
        {!isLoading && assessment && (
          <div className="space-y-4">
            {/* Health Score */}
            <div className="flex items-center gap-4">
              <div className="relative w-16 h-16 flex-shrink-0">
                <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
                  <circle cx="32" cy="32" r="26" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" />
                  <circle
                    cx="32" cy="32" r="26" fill="none"
                    stroke={healthColor} strokeWidth="6"
                    strokeDasharray={`${(assessment?.healthScore / 100) * 163.4} 163.4`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-bold" style={{ color: healthColor }}>{assessment?.healthScore}</span>
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-bold" style={{ color: healthColor }}>{assessment?.overallHealth}</span>
                  <span className="text-xs text-[#64748B]">Goal Health</span>
                </div>
                <p className="text-[#94A3B8] text-xs leading-relaxed">{assessment?.summary}</p>
              </div>
            </div>

            {/* On Track / At Risk */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
                <div className="flex items-center gap-1.5 mb-2">
                  <Icon name="CheckCircle" size={13} color="#10B981" />
                  <span className="text-xs font-semibold text-[#10B981]">On Track</span>
                </div>
                {assessment?.onTrackGoals?.length > 0 ? (
                  assessment?.onTrackGoals?.map((g, i) => (
                    <p key={i} className="text-xs text-[#94A3B8] truncate">• {g}</p>
                  ))
                ) : (
                  <p className="text-xs text-[#475569]">None yet</p>
                )}
              </div>
              <div className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}>
                <div className="flex items-center gap-1.5 mb-2">
                  <Icon name="AlertTriangle" size={13} color="#EF4444" />
                  <span className="text-xs font-semibold text-[#EF4444]">At Risk</span>
                </div>
                {assessment?.atRiskGoals?.length > 0 ? (
                  assessment?.atRiskGoals?.map((g, i) => (
                    <p key={i} className="text-xs text-[#94A3B8] truncate">• {g}</p>
                  ))
                ) : (
                  <p className="text-xs text-[#475569]">None — great!</p>
                )}
              </div>
            </div>

            {/* Action Plans for at-risk goals */}
            {assessment?.actionPlans?.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs font-semibold text-[#94A3B8] flex items-center gap-1.5">
                  <Icon name="Zap" size={12} color="#F59E0B" />
                  Action Plans
                </p>
                {assessment?.actionPlans?.map((plan, idx) => (
                  <div key={idx} className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)' }}>
                    <p className="text-xs font-semibold text-[#F59E0B] mb-2">{plan?.goalTitle}</p>
                    {plan?.steps?.map((step, si) => (
                      <div key={si} className="flex items-start gap-1.5 mb-1">
                        <span className="text-[#F59E0B] text-xs font-bold flex-shrink-0">{si + 1}.</span>
                        <p className="text-[#94A3B8] text-xs leading-relaxed">{step}</p>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
